/**
 * Theme preference: system, light or dark.
 *
 * Three states rather than a boolean, because "follow the OS" is a real answer
 * and the common one — a bookkeeper whose machine goes dark at dusk expects the
 * app to come with it. Only an explicit choice is persisted; `system` means we
 * keep listening to `prefers-color-scheme`.
 *
 * The class is applied to <html> by the inline script in index.html before first
 * paint (see the comment there), so this module's job on mount is only to keep
 * it in step with later changes.
 */
import { useCallback, useEffect, useState } from "react";

export type ThemeChoice = "system" | "light" | "dark";

export const THEME_KEY = "onecpa.theme";

/** The order the rail's single button steps through. */
export const THEME_CYCLE: ThemeChoice[] = ["system", "light", "dark"];

/** Label and icon per choice, shared by the rail button and the Settings control. */
export const THEME_META: Record<ThemeChoice, { label: string; icon: "desktop" | "sun" | "moon" }> = {
  system: { label: "לפי המערכת", icon: "desktop" },
  light: { label: "בהיר", icon: "sun" },
  dark: { label: "כהה", icon: "moon" },
};

function isChoice(value: string | null): value is ThemeChoice {
  return value === "system" || value === "light" || value === "dark";
}

export function readThemeChoice(): ThemeChoice {
  const stored = localStorage.getItem(THEME_KEY);
  return isChoice(stored) ? stored : "system";
}

function prefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** The theme actually on screen once `system` has been resolved. */
export function resolveTheme(choice: ThemeChoice): "light" | "dark" {
  return choice === "system" ? (prefersDark() ? "dark" : "light") : choice;
}

function applyTheme(choice: ThemeChoice): void {
  document.documentElement.classList.toggle("dark", resolveTheme(choice) === "dark");
}

export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(() => readThemeChoice());

  // Kept in a state value rather than read at render time so a change to the OS
  // preference re-renders the control that displays it.
  const [resolved, setResolved] = useState<"light" | "dark">(() => resolveTheme(choice));

  useEffect(() => {
    applyTheme(choice);
    setResolved(resolveTheme(choice));

    // Only `system` cares about the OS flipping underneath it.
    if (choice !== "system") return;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      applyTheme("system");
      setResolved(resolveTheme("system"));
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [choice]);

  const choose = useCallback((next: ThemeChoice) => {
    // `system` is the absence of a preference, so it is stored as one: removing
    // the key is what lets a future default change reach this user.
    if (next === "system") localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, next);
    setChoice(next);
  }, []);

  return { choice, resolved, setTheme: choose };
}
